import { create } from "zustand";
import { io, Socket } from "socket.io-client";
import { fetchBackupDate, fetchProjects, recoverProjects, type Project } from "../utils/api";
import { handleApiError } from "../utils/errorHandler";
import { ALMI_BASE_URL } from "../utils/constants";

interface JobProgress {
  step: string;
  progress: number;
  message?: string;
  [key: string]: any;
}

interface RecoveryJob {
  id: string;
  isFinished: boolean;
  isSuccess: boolean;
  isRunning: boolean;
  progress: JobProgress | null;
  logs: string[];
}

interface ProjectState {
  projects: Project[];
  selectedProjects: string[];
  projectStatus: "project-success" | "project-error" | null;

  currentJob: RecoveryJob | null;

  loading: boolean;
  selectLoading: boolean;
  error: string | null;
  selectError: string | null;

  recoveryPopupMinimized: boolean;

  fetchProjects: () => Promise<void>;
  recover: (
    server: string,
    filtered: string[],
    forceMove?: boolean,
    dryRun?: boolean,
  ) => Promise<void>;
  clearRecovery: () => void;
  setSelectedProjects: (selected: string[]) => void;

  initSocket: () => void;
  subscribeToJob: (jobId: string) => void;
  unsubscribeFromJob: () => void;
  updateJobFromPayload: (payload: any) => void;

  setRecoveryPopupMinimized: (minimized: boolean) => void;

  backupDate: string | null;
  backupDateLoading: boolean;
  fetchBackupDate: () => Promise<void>;
}

let socket: Socket | null = null;

export const useProjectsStore = create<ProjectState>()((set, get) => ({
  projects: [],
  selectedProjects: [],
  projectStatus: null,

  currentJob: null,

  loading: false,
  selectLoading: false,
  error: null,
  selectError: null,

  recoveryPopupMinimized: false,

  clearRecovery: () => {
    console.log("[STORE] clearRecovery called");
    get().unsubscribeFromJob();
    set({
      projectStatus: null,
      selectError: null,
      currentJob: null,
      selectLoading: false,
    });
  },
  backupDate: null as string | null,
  backupDateLoading: false,

  fetchBackupDate: async () => {
    set({ backupDateLoading: true });

    try {
      const date = await fetchBackupDate();
      set({
        backupDate: date || null,
        backupDateLoading: false,
      });
    } catch (err) {
      console.warn("Ошибка при загрузке даты бэкапа:", err);
      set({
        backupDate: null,
        backupDateLoading: false,
      });
    }
  },
  fetchProjects: async () => {
    set({ loading: true, error: null });
    try {
      const projects = await fetchProjects();
      set({ projects, loading: false });
      if (projects.length === 0) {
        set({ error: "Ничего не найдено." });
      }
    } catch (err: unknown) {
      const errorMessage = handleApiError(
        err,
        "Ошибка загрузки проектов. Попробуйте позже.",
      );
      set({ error: errorMessage, loading: false });
    }
  },

  recover: async (
    server: string,
    filtered: string[] = [],
    forceMove = true,
    dryRun = false,
  ) => {
    console.log("[STORE] recover() вызван →", {
      server,
      filtered,
      forceMove,
      dryRun,
    });

    set({
      selectLoading: true,
      selectError: null,
      currentJob: null,
      projectStatus: null,
    });

    try {
      const response = await recoverProjects(
        server,
        filtered,
        forceMove,
        dryRun,
      );

      if (!response.success || !response.jobId) {
        throw new Error(response.error || "Не удалось запустить задачу");
      }

      const jobId = response.jobId;

      const initialJob: RecoveryJob = {
        id: jobId,
        isFinished: false,
        isSuccess: false,
        isRunning: true,
        progress: {
          step: "starting",
          progress: 5,
          message: "Запускаем восстановление...",
        },
        logs: [
          `[${new Date().toLocaleTimeString()}] Запускаем восстановление проектов, id задачи: ${jobId}`,
        ],
      };

      console.log(`[STORE] Установлен initialJob для задачи ${jobId}`);

      set({
        selectLoading: false,
        currentJob: initialJob,
      });

      get().subscribeToJob(jobId);
    } catch (err: unknown) {
      const errorMessage = handleApiError(
        err,
        "Ошибка восстановления проектов. Попробуйте позже.",
      );
      set({
        selectError: errorMessage,
        selectLoading: false,
        currentJob: null,
      });
    }
  },

  setSelectedProjects: (selected) => set({ selectedProjects: selected }),

  initSocket: () => {
    if (socket) return;
    socket = io(ALMI_BASE_URL, {
      path: "/socket.io",
      transports: ["websocket"],
      withCredentials: true,
      forceNew: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });

    socket.on("connect", () => console.log(`[SOCKET.IO] Подключено`));
    socket.on("connect_error", (err) =>
      console.error("[SOCKET.IO] Connect error:", err),
    );
    socket.on("disconnect", (reason) =>
      console.warn("[SOCKET.IO] Отключён:", reason),
    );
  },

  subscribeToJob: (jobId: string) => {
    console.log(`[STORE] subscribeToJob → ${jobId}`);
    get().unsubscribeFromJob();

    get().initSocket();
    const currentSocket = socket!;
    if (!currentSocket) return;

    const handleProgress = (data: any) => {
      console.log("[STORE] ← progress event", data);

      // if (data.state === 'completed' || data.isFinished === true) {
      //     console.log("[STORE] Игнорируем completed через progress event");
      //     return;
      // }
      get().updateJobFromPayload({ ...data, id: data.jobId || jobId });
    };

    const handleDone = (data: any) => {
      console.log("[STORE] ← done event", data);
      get().updateJobFromPayload({
        ...data,
        id: data.jobId || jobId,
        isFinished: true,
        isSuccess: true,
        isRunning: false,
        progress: {
          step: "completed",
          progress: 100,
          message: "Восстановление завершено",
        },
      });
    };

    const handleError = (errData: any) => {
      console.log("[STORE] ← error event", errData);
      get().updateJobFromPayload({
        ...errData,
        id: errData?.jobId || jobId,
        isFinished: true,
        isSuccess: false,
        isRunning: false,
      });
    };

    (currentSocket as any)._currentHandlers = {
      handleProgress,
      handleDone,
      handleError,
      jobId,
    };

    currentSocket.on("progress", handleProgress);
    currentSocket.on("done", handleDone);
    currentSocket.on("error", handleError);

    currentSocket.emit("join", jobId, () => {
      console.log("[SOCKET] joined room", jobId);
      currentSocket.emit("getJobState", jobId);
    });
  },

  unsubscribeFromJob: () => {
    if (!socket) return;
    console.log("[STORE] unsubscribeFromJob");
    const handlers = (socket as any)._currentHandlers;
    if (handlers) {
      socket.off("progress", handlers.handleProgress);
      socket.off("done", handlers.handleDone);
      socket.off("error", handlers.handleError);
      delete (socket as any)._currentHandlers;
    }
  },

  updateJobFromPayload: (payload: any) => {
    console.log("[STORE] updateJobFromPayload → raw payload:", payload);
    const current = get().currentJob;

    const currentLogs = current?.logs ?? [];

    const timestamp = new Date().toLocaleTimeString();
    let messageToAdd = "";

    if (payload.progress?.message) {
      messageToAdd = payload.progress.message;
    } else if (payload.message) {
      messageToAdd = payload.message;
    } else if (payload.error) {
      messageToAdd = `ERROR: ${payload.error}`;
    }

    const newLogs = [...currentLogs];

    if (messageToAdd) {
      const formattedMsg = `[${timestamp}] ${messageToAdd}`;
      if (!newLogs.some((log) => log.includes(messageToAdd))) {
        newLogs.push(formattedMsg);
      }
    }

    const job: RecoveryJob = {
      id: payload.jobId || current?.id || "",
      isFinished: payload.isFinished ?? current?.isFinished ?? false,
      isSuccess: payload.isSuccess ?? current?.isSuccess ?? false,
      isRunning:
        payload.isRunning ??
        !(payload.isFinished ?? current?.isFinished ?? false),
      progress: payload.step
        ? payload
        : (payload.progress ?? current?.progress ?? null),
      logs: newLogs,
    };

    if (payload.step === "completed") {
      job.isFinished = true;
      job.isSuccess = true;
      job.isRunning = false;
    }

    console.log(`[STORE] Обновлён job ${job.id}.`);

    set({
      currentJob: job,
      ...(job.isFinished && {
        projectStatus: job.isSuccess ? "project-success" : "project-error",
      }),
    });
  },

  setRecoveryPopupMinimized: (minimized: boolean) =>
    set({ recoveryPopupMinimized: minimized }),
}));
